import {
  DirectMessagesResponse,
  DismissItemsType,
  ChatModeEnum,
  JobChatResponse,
  MarkAllJobChatsAsReadResponse,
  MarkAllDirectChatsAsReadResponse,
  ReceivedReviewsResponse,
  ReviewRequestsResponse,
  ReviewsLeftResponse,
} from '@company/types/comm-hub.type';
import { privateApi } from '@services/api';
import IResultResponse from '@th-types/resultResponse.type';
import { buildComHubQueryParams } from '@utils/ComHubUtils';
import { isAxiosError } from 'axios';

const COMM_HUB_MAIN_URL = 'company/comhub';

export async function getReceivedReviewsNewCounter() {
  const url = `${COMM_HUB_MAIN_URL}/reviews/count-new`;
  try {
    const response = await privateApi.get(url);
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching received reviews counter'
      );
    }
    // Let the caller handle the error
    throw error;
  }
}

export async function getDirectMessagesNewCounter() {
  const url = `company/direct-chats/count-unread`;
  try {
    const response = await privateApi.get(url);
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching direct messages counter'
      );
    }
    // Let the caller handle the error
    throw error;
  }
}

export async function getJobChatsNewCounter() {
  const url = `company/chat/job-chats/count-unread`;
  try {
    const response = await privateApi.get(url);
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching job messages counter'
      );
    }
    // Let the caller handle the error
    throw error;
  }
}

export async function getReviewRequestsNewCounter() {
  const url = `${COMM_HUB_MAIN_URL}/review-requests/count-new`;
  try {
    const response = await privateApi.get(url);
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching review requests counter'
      );
    }
    // Let the caller handle the error
    throw error;
  }
}

export async function getPendingReviewsNewCounter() {
  const url = `${COMM_HUB_MAIN_URL}/pending-reviews/count-new`;
  try {
    const response = await privateApi.get(url);
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching pending reviews counter'
      );
    }
    // Let the caller handle the error
    throw error;
  }
}

export async function getReceivedReviews(
  page: number,
  itemsPerPage: number,
  searchText?: string,
  dateRange?: { startDate: Date | undefined; endDate: Date | undefined },
  isNew?: boolean
): Promise<ReceivedReviewsResponse> {
  const baseUrl = `${COMM_HUB_MAIN_URL}/reviews`;
  const queryParams = buildComHubQueryParams({
    page,
    itemsPerPage,
    searchText,
    dateRange,
    isNew,
  });
  const url = `${baseUrl}?${queryParams}`;
  try {
    const response = await privateApi.request({
      method: 'GET',
      url,
    });
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching received reviews'
      );
    }
    throw error;
  }
}

export async function getReviewRequests(
  page: number,
  itemsPerPage: number,
  searchText?: string,
  dateRange?: { startDate: Date | undefined; endDate: Date | undefined },
  isNew?: boolean
): Promise<ReviewRequestsResponse> {
  const baseUrl = `${COMM_HUB_MAIN_URL}/review-requests`;
  const queryParams = buildComHubQueryParams({
    page,
    itemsPerPage,
    searchText,
    dateRange,
    isNew,
  });
  const url = `${baseUrl}?${queryParams}`;
  try {
    const response = await privateApi.request({
      method: 'GET',
      url,
    });
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching review requests'
      );
    }
    throw error;
  }
}

export async function getPendingReviews(
  page: number,
  itemsPerPage: number,
  searchText?: string,
  dateRange?: { startDate: Date | undefined; endDate: Date | undefined },
  isNew?: boolean
): Promise<ReviewRequestsResponse> {
  const baseUrl = `${COMM_HUB_MAIN_URL}/pending-reviews`;
  const queryParams = buildComHubQueryParams({
    page,
    itemsPerPage,
    searchText,
    dateRange,
    isNew,
  });
  const url = `${baseUrl}?${queryParams}`;
  try {
    const response = await privateApi.get(url);
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching pending reviews'
      );
    }
    throw error;
  }
}

export async function markAllJobChatsAsRead(): Promise<MarkAllJobChatsAsReadResponse> {
  const url = `chat/job-chats/mark-all-read`;
  try {
    const response = await privateApi.post<MarkAllJobChatsAsReadResponse>(
      url,
      {}
    );
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.response?.data?.errorMessage ||
          'Error marking job messages as read'
      );
    }
    throw error;
  }
}

export async function markAllDirectChatsAsRead(): Promise<MarkAllDirectChatsAsReadResponse> {
  const url = `company/direct-chats/mark-all-read`;
  try {
    const response =
      await privateApi.post<MarkAllDirectChatsAsReadResponse>(url);
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.response?.data?.errorMessage ||
          'Error marking direct messages as read'
      );
    }
    throw error;
  }
}

export async function getReviewsAndEndorsements(
  page: number,
  itemsPerPage: number,
  searchText?: string,
  dateRange?: { startDate: Date | undefined; endDate: Date | undefined }
): Promise<ReviewsLeftResponse> {
  const baseUrl = `${COMM_HUB_MAIN_URL}/given-reviews`;
  const queryParams = buildComHubQueryParams({
    page,
    itemsPerPage,
    searchText,
    dateRange,
  });
  const url = `${baseUrl}?${queryParams}`;
  try {
    const response = await privateApi.request({
      method: 'GET',
      url,
    });
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching reviews and endorsements'
      );
    }
    throw error;
  }
}

export async function dismissItemsFromNew(itemsToDismiss: DismissItemsType) {
  const url = `comhub/mark-as-dismissed`;

  try {
    const response = await privateApi.post(url, itemsToDismiss);
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error dismissing items'
      );
    }
    // Let the caller handle the error
    throw error;
  }
}

export const replyRating = async (ratingId: number, replyComment: string) => {
  try {
    const url = `${COMM_HUB_MAIN_URL}/reviews/${ratingId}/reply`;
    return await privateApi.post<IResultResponse>(url, {
      replyComment,
    });
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export async function getGivenEndorsement(endorsementCaseId: number) {
  const url = `company/endorsements/${endorsementCaseId}`;

  try {
    const response = await privateApi.get(url);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = isAxiosError(error)
      ? error.message
      : 'Error fetching given endorsement';
    throw new Error(errorMessage);
  }
}

export async function getDirectMessages(
  page: number,
  itemsPerPage: number,
  searchText?: string,
  dateRange?: { startDate: Date | undefined; endDate: Date | undefined },
  isUnread?: boolean,
  mode?: ChatModeEnum | null
): Promise<DirectMessagesResponse> {
  const baseUrl = `company/direct-chats`;
  const queryParams = buildComHubQueryParams({
    page,
    itemsPerPage,
    searchText,
    dateRange,
    isUnread, // Use isUnread for direct messages
    mode,
  });
  const url = `${baseUrl}?${queryParams}`;
  try {
    const response = await privateApi.request({
      method: 'GET',
      url,
    });

    // Check if the response indicates an error
    if (response.data && response.data.success === false) {
      throw new Error(
        response.data.message ||
          response.data.errorMessage ||
          'Error fetching direct messages'
      );
    }

    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching direct messages'
      );
    }
    throw error;
  }
}

export async function getJobChats(
  page: number,
  itemsPerPage: number,
  searchText?: string,
  dateRange?: { startDate: Date | undefined; endDate: Date | undefined },
  isNew?: boolean,
  mode?: ChatModeEnum | null
): Promise<JobChatResponse> {
  const baseUrl = `company/chat/job-chats`;
  const queryParams = buildComHubQueryParams({
    page,
    itemsPerPage,
    searchText,
    dateRange,
    isUnread: isNew, // Use isUnread for job messages
    mode,
  });
  const url = `${baseUrl}?${queryParams}`;
  try {
    const response = await privateApi.request({
      method: 'GET',
      url,
    });

    // Check if the response indicates an error
    if (response.data && response.data.success === false) {
      throw new Error(
        response.data.message ||
          response.data.errorMessage ||
          'Error fetching job messages'
      );
    }

    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.errorMessage ||
          'Error fetching job messages'
      );
    }
    throw error;
  }
}
